import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useProjects } from '../hooks/useProjects';
import { useLocalStorage } from '../hooks/useLocalStorage';

interface Project {
  id: string;
  name: string;
  description?: string;
  websiteUrl?: string;
  industry?: string;
  isActive?: boolean;
  ga4PropertyId?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface ProjectContextType {
  selectedProject: Project | null;
  selectedProjectId: string | null;
  projects: Project[];
  isLoading: boolean;
  setSelectedProject: (project: Project | null) => void;
  setSelectedProjectId: (projectId: string | null) => void;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

interface ProjectProviderProps {
  children: ReactNode;
}

export function ProjectProvider({ children }: ProjectProviderProps) {
  const { projects, isLoading } = useProjects();
  const [selectedProjectId, setStoredSelectedProjectId] = useLocalStorage<string | null>('selectedProjectId', null);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  // Update selected project when projectId changes or projects load
  useEffect(() => {
    if (!isLoading && projects.length > 0) {
      let projectToSelect: Project | null = null;

      if (selectedProjectId) {
        // Try to find the stored selected project
        projectToSelect = projects.find(p => p.id === selectedProjectId) || null;
      }

      // If no stored project or stored project not found, select the first project
      if (!projectToSelect) {
        projectToSelect = projects[0] || null;
        if (projectToSelect) {
          setStoredSelectedProjectId(projectToSelect.id);
        }
      }

      setSelectedProject(projectToSelect);
    } else if (!isLoading && projects.length === 0) {
      // No projects available
      setSelectedProject(null);
      setStoredSelectedProjectId(null);
    }
  }, [projects, selectedProjectId, isLoading, setStoredSelectedProjectId]);

  const handleSetSelectedProject = (project: Project | null) => {
    setSelectedProject(project);
    setStoredSelectedProjectId(project?.id || null);
  };

  const handleSetSelectedProjectId = (projectId: string | null) => {
    const project = projectId ? projects.find(p => p.id === projectId) || null : null;
    setSelectedProject(project);
    setStoredSelectedProjectId(projectId);
  };

  const value: ProjectContextType = {
    selectedProject,
    selectedProjectId: selectedProject?.id || null,
    projects,
    isLoading,
    setSelectedProject: handleSetSelectedProject,
    setSelectedProjectId: handleSetSelectedProjectId,
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
}

export function useProjectContext() {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProjectContext must be used within a ProjectProvider');
  }
  return context;
} 